import React, { useEffect } from 'react'
import useAsyncStorageState from '@hooks/useAsyncStorageState'
import Button from '@components/Button'
import { LinearGradient } from 'expo-linear-gradient'
import Card from '@components/Card'
import Header from '@components/Header'
import Input from '@components/Input'
import Picker from '@components/Picker'
import FieldSet from '@components/FieldSet'
import UPDATE_USER_BY_ID from '@lib/queries/updateUserById'
import useMutation from '@hooks/useMutation'
import useSentry from '@hooks/useSentry'
import { useSnackbar, SnackbarThemes } from '@context/SnackbarContext'
import { useCurrentUser } from '@context/CurrentUserContext'
import { colors } from '@lib/colors'
import { i18n } from '@lib/i18n'
import { Dimensions, Platform, ScrollView, StyleSheet, Text, View } from 'react-native'
import MainLayout from '@layouts/MainLayout'
import useNavigation from '@hooks/useNavigation'

const COPY = i18n.en.PROFILE

interface SaveProfilePayload {
  id: string
  [key: string]: string
}

interface ProfileData {
  firstName: string
  lastName: string
  title: string
  email: string
  phone: string
  businessName: string
  address: string
  suite: string
  city: string
  state: string
  zipCode: string
  preferredCraftsmanship: string
  pricingPreference: string
  secondaryFirstName: string
  secondaryLastName: string
  secondaryTitle: string
  secondaryEmail: string
  secondaryPhone: string
}

export const defaultProfileData: ProfileData = {
  firstName: '',
  lastName: '',
  title: '',
  email: '',
  phone: '',
  //
  businessName: '',
  address: '',
  suite: '',
  city: '',
  state: '',
  zipCode: '',
  //
  preferredCraftsmanship: 'medium',
  pricingPreference: 'medium',
  //
  secondaryFirstName: '',
  secondaryLastName: '',
  secondaryTitle: '',
  secondaryEmail: '',
  secondaryPhone: '',
}

export default function () {
  const { mutate } = useMutation(UPDATE_USER_BY_ID)
  const [profileData, setProfileData, profileDataLoading] = useAsyncStorageState<ProfileData>(
    'profileData', defaultProfileData,
  )
  const { userAuthenticated, currentUser, setCurrentUser } = useCurrentUser()
  const { setSnackbarMessage } = useSnackbar()
  const { captureException } = useSentry()
  const navigation = useNavigation()

  useEffect(() => {
    navigation.setOptions({ tabBarStyle: { display: 'none' } })
  }, [])

  useEffect(() => {
    setProfileData({
      ...profileData,
      email: currentUser?.email || '',
      firstName: currentUser?.firstName || '',
      lastName: currentUser?.lastName || '',
      phone: currentUser?.phone || '',
    })
  }, [profileDataLoading, currentUser])

  const updateProfile = (key: keyof ProfileData) => {
    return (value?: string | number) => {
      if (value === undefined) return
      setProfileData({ ...profileData, [key]: String(value) } as ProfileData)
    }
  }

  const saveProfileData = async () => {
    if (!userAuthenticated) {
      setSnackbarMessage('Profile successfully updated', SnackbarThemes.success)
      return
    }

    try {
      // Build payload
      const payload: SaveProfilePayload = { id: '' }
      if (currentUser?.id) {
        payload.id = currentUser.id
      } else {
        throw new Error('Invalid user ID')
      }
      if (currentUser?.email) payload.email = currentUser.email
      if (profileData?.firstName) payload.firstName = profileData.firstName
      if (profileData?.lastName) payload.lastName = profileData.lastName
      if (profileData?.phone) payload.phone = profileData.phone

      // eslint-disable-next-line
      const { data, error } = await mutate(
        payload,
        { Authorization: `Bearer ${currentUser?.jwtToken}` },
      )

      if (error) {
        setSnackbarMessage('An error occurred, please try again', SnackbarThemes.error)
        return
      }
      setCurrentUser({ ...currentUser, ...profileData })
      setSnackbarMessage('Profile successfully updated', SnackbarThemes.success)
    } catch (error) {
      captureException(error)
      setSnackbarMessage('An error occurred, please try again', SnackbarThemes.error)
    }
  }

  return (
    <MainLayout hideBackButton>
      <ScrollView style={ styles.container }>
        <Header>{ COPY.HEADER }</Header>
        <Card style={ styles.formCard }>
          {/**
           * 'About me' section
           */}
          <Text style={ StyleSheet.compose(styles.formSectionHeader, styles.formSectionHeaderFirst) }>{ COPY.ABOUT_ME.HEADER }</Text>
          <FieldSet>
            <Input
              onChange={ updateProfile('firstName') }
              placeholder={ COPY.ABOUT_ME.FIRST_NAME_PLACEHOLDER }
              value={ profileData?.firstName }
            />
            <Input
              onChange={ updateProfile('lastName') }
              placeholder={ COPY.ABOUT_ME.LAST_NAME_PLACEHOLDER }
              value={ profileData?.lastName }
            />
            <Input
              onChange={ updateProfile('title') }
              placeholder={ COPY.ABOUT_ME.TITLE_PLACEHOLDER }
              value={ profileData?.title }
            />
            <Input
              onChange={ updateProfile('email') }
              placeholder={ COPY.ABOUT_ME.EMAIL_PLACEHOLDER }
              value={ profileData?.email }
            />
            <Input
              onChange={ updateProfile('phone') }
              placeholder={ COPY.ABOUT_ME.PHONE_PLACEHOLDER }
              value={ profileData?.phone }
            />
          </FieldSet>

          {/**
           * 'My business' section
           */}
          <Text style={ styles.formSectionHeader }>{ COPY.MY_BUSINESS.HEADER }</Text>
          <FieldSet>
            <Input
              onChange={ updateProfile('businessName') }
              placeholder={ COPY.MY_BUSINESS.BUSINESS_NAME_PLACEHOLDER }
              value={ profileData?.businessName }
            />
            <Input
              onChange={ updateProfile('address') }
              placeholder={ COPY.MY_BUSINESS.ADDRESS_PLACEHOLDER }
              value={ profileData?.address }
            />
            <Input
              onChange={ updateProfile('suite') }
              placeholder={ COPY.MY_BUSINESS.SUITE_PLACEHOLDER }
              value={ profileData?.suite }
            />
            <Input
              onChange={ updateProfile('city') }
              placeholder={ COPY.MY_BUSINESS.CITY_PLACEHOLDER }
              value={ profileData?.city }
            />
            <Input
              onChange={ updateProfile('state') }
              placeholder={ COPY.MY_BUSINESS.STATE_PLACEHOLDER }
              value={ profileData?.state }
            />
            <Input
              onChange={ updateProfile('zipCode') }
              placeholder={ COPY.MY_BUSINESS.ZIP_CODE_PLACEHOLDER }
              value={ profileData?.zipCode }
            />
          </FieldSet>

          {/**
           * 'My preferences' section
           */}
          <Text style={ styles.formSectionHeader }>{ COPY.PREFERENCES.HEADER }</Text>
          <FieldSet>
            <Picker
              value={ profileData?.preferredCraftsmanship }
              onValueChange={ (value) => {
                setProfileData({ ...profileData, preferredCraftsmanship: value as string } as ProfileData)
              }}
              label={ COPY.PREFERENCES.PREFERRED_CRAFTSMANSHIP_PLACEHOLDER }
              items={[
                { label: 'Low', value: 'low' },
                { label: 'Medium', value: 'medium' },
                { label: 'High', value: 'high' },
                { label: 'Very high', value: 'veryhigh' },
              ]}
            />

            <Picker
              value={ profileData?.pricingPreference }
              onValueChange={ (value) => {
                setProfileData({ ...profileData, pricingPreference: value as string } as ProfileData)
              }}
              label={ COPY.PREFERENCES.PRICING_PREFERENCE_PLACEHOLDER }
              items={[
                { label: 'Low', value: 'low' },
                { label: 'Medium', value: 'medium' },
                { label: 'High', value: 'high' },
                { label: 'Very high', value: 'veryhigh' },
              ]}
            />
          </FieldSet>

          {/**
           * 'Secondary contact' section
           */}
          <Text style={ styles.formSectionHeader }>{ COPY.SECONDARY_CONTACT.HEADER }</Text>
          <FieldSet fieldStyle={ styles.formSectionLast }>
            <Input
              onChange={ updateProfile('secondaryFirstName') }
              placeholder={ COPY.SECONDARY_CONTACT.FIRST_NAME_PLACEHOLDER }
              value={ profileData?.secondaryFirstName }
            />
            <Input
              onChange={ updateProfile('secondaryLastName') }
              placeholder={ COPY.SECONDARY_CONTACT.LAST_NAME_PLACEHOLDER }
              value={ profileData?.secondaryLastName }
            />
            <Input
              onChange={ updateProfile('secondaryTitle') }
              placeholder={ COPY.SECONDARY_CONTACT.TITLE_PLACEHOLDER }
              value={ profileData?.secondaryTitle }
            />
            <Input
              onChange={ updateProfile('secondaryEmail') }
              placeholder={ COPY.SECONDARY_CONTACT.EMAIL_PLACEHOLDER }
              value={ profileData?.secondaryEmail }
            />
            <Input
              onChange={ updateProfile('secondaryPhone') }
              placeholder={ COPY.SECONDARY_CONTACT.PHONE_PLACEHOLDER }
              value={ profileData?.secondaryPhone }
            />
          </FieldSet>
        </Card>
      </ScrollView>
      <View style={styles.bottomButtonContainer}>
        <LinearGradient
          colors={[colors.grayTransparent, colors.gray[50]]}
          style={styles.bottomButtonTopGradient}
        />
        <LinearGradient
          colors={[colors.gray[50], colors.gray[50]]}
        >
          <Button
            label='Update Profile'
            buttonStyles={ styles.bottomButton }
            onPress={ saveProfileData }
          />
        </LinearGradient>
      </View>
    </MainLayout>
  )
}

const styles = StyleSheet.create({
  bottomButtonContainer: {
    position: 'absolute',
    bottom: Platform.OS === 'ios' ? 30 : 0,
    left: 0,
    right: 0,
  },
  bottomButtonTopGradient: {
    height: 10,
    width: '100%',
  },
  bottomButton: {
    alignSelf: 'stretch',
    backgroundColor: colors.green[400],
    marginLeft: 20,
    marginRight: 20,
    marginTop: 10,
    marginBottom: 10,
  },
  container: {
    flex: 1,
    paddingTop: 20,
    width: '100%',
  },
  formCard: {
    width: Dimensions.get('window').width - 40,
    margin: 20,
    marginBottom: 120,
  },
  formSectionHeader: {
    color: colors.black,
    fontSize: 16,
    fontWeight: '600',
    marginTop: 20,
  },
  formSectionHeaderFirst: {
    marginTop: 0,
  },
  formSectionLast: {
    marginBottom: 0,
  },
})
